"use client";

import React, { useEffect, useState } from "react";
import Container from "@mui/material/Container";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import Card from "@mui/material/Card";
import CardContent from "@mui/material/CardContent";
import { PLATFORM_NAME } from "@/lib/constants";
import GoogleSignInButton from "@/components/shared/ui/GoogleSignInButton";
import { useT } from "@/i18n/useT";
import {
  Alert,
  CircularProgress,
  Chip,
  CardActions,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  TextField,
  DialogActions,
} from "@mui/material";
import { Stack } from "@mui/system";
import { useAuth } from "@/lib/useAuth";
import { School } from "@/types/school";
import { useNavigation } from "@/lib/navigation";
import {
  getFirestore,
  collection,
  query,
  where,
  onSnapshot,
} from "firebase/firestore";
import { schoolRepository } from "@/lib/firestore/repositories/SchoolRepository";

interface PlatformLandingPageProps {
  onLogin: () => void;
}

/**
 * Main platform landing page for unauthenticated users visiting PLATFORM_NAME.com
 */
export default function PlatformLandingPage({
  onLogin,
}: PlatformLandingPageProps) {
  const { user } = useAuth();
  const [error, setError] = useState<string | null>(null);
  const [schools, setSchools] = React.useState<School[]>([]);
  const [schoolLoading, setLoadingSchools] = React.useState(false);
  const [createLoading, setCreateLoading] = React.useState(false);
  const tActions = useT("ui.actions");
  const t = useT("routes.platformLandingPage");
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const navigation = useNavigation();
  const [newSchool, setNewSchool] = useState({
    name: "",
    slug: "",
  });

  // Real-time listener for user's schools
  useEffect(() => {
    if (!user || !user.uid) return;
    setError(null);
    setLoadingSchools(true);
    const db = getFirestore();
    // Listen for schools where user is a member (status: active)
    const memberQuery = query(
      collection(db, "schoolMembers"),
      where("email", "==", user.email || ""),
      where("status", "==", "active")
    );
    const unsubscribe = onSnapshot(
      memberQuery,
      async (memberSnapshot) => {
        if (memberSnapshot.empty) {
          setSchools([]);
          setLoadingSchools(false);
          return;
        }
        // Get schoolIds
        const schoolIds = memberSnapshot.docs.map((doc) => doc.data().schoolId);
        // Use Firestore getDoc for each school
        const { getDoc, doc } = await import("firebase/firestore");
        const schoolDocs = await Promise.all(
          schoolIds.map((schoolId) => getDoc(doc(db, "schools", schoolId)))
        );
        // Ensure returned objects match School type
        const userSchools: School[] = schoolDocs
          .filter((snap) => snap.exists())
          .map((snap) => {
            const data = snap.data();
            return {
              id: snap.id,
              name: data?.name || "",
              slug: data?.slug || "",
              ownerId: data?.creatorId || "",
              isActive: data?.isActive ?? true,
              createdAt: data?.createdAt ?? null,
            };
          });
        setSchools(userSchools);
        setLoadingSchools(false);
      },
      (err) => {
        setError(err.message || "Failed to load schools");
        setLoadingSchools(false);
      }
    );
    return () => unsubscribe();
  }, [user]);

  const handleCreateSchool = async () => {
    if (!newSchool.name.trim() || !newSchool.slug.trim()) {
      setError("Please provide both name and slug");
      return;
    }
    if (!user) {
      setError("You must be logged in to create a school");
      return;
    }
    setCreateLoading(true);
    setError(null);
    try {
      await schoolRepository.createSchool(
        user.email || "",
        user.uid,
        newSchool.name,
        newSchool.slug
      );
      setShowCreateDialog(false);
      setNewSchool({ name: "", slug: "" });
      // No need to manually reload schools; onSnapshot will update
    } catch (err) {
      const errorMessage =
        err instanceof Error ? err.message : "Failed to create school";
      setError(errorMessage);
    } finally {
      setCreateLoading(false);
    }
  };

  const handleSelectSchool = async (school: School) => {
    if (!user) {
      console.warn(
        "🏠 [HomePage] School selection aborted - no user logged in"
      );
      return;
    }

    navigation.selectSchool(school, user.uid, ["student"], {
      reason: "School selection from homepage",
    });
  };

  const generateSlug = (name: string) => {
    return name
      .toLowerCase()
      .replace(/[^a-z0-9\s-]/g, "") // Remove special characters
      .replace(/\s+/g, "-") // Replace spaces with hyphens
      .replace(/-+/g, "-") // Replace multiple hyphens with single
      .replace(/^-|-$/g, ""); // Remove leading/trailing hyphens
  };

  const handleNameChange = (name: string) => {
    setNewSchool((prev) => ({
      ...prev,
      name,
    }));
  };

  const handleSlugChange = (slugInput: string) => {
    const slug = generateSlug(slugInput);
    setNewSchool((prev) => ({
      ...prev,
      slug,
    }));
  };

  // ...existing code...

  const whyChooseCards = [
    {
      title: t("whyChooseCard1.title"),
      description: t("whyChooseCard1.description"),
    },
    {
      title: t("whyChooseCard2.title"),
      description: t("whyChooseCard2.description"),
    },
    {
      title: t("whyChooseCard3.title"),
      description: t("whyChooseCard3.description"),
    },
    {
      title: t("whyChooseCard4.title"),
      description: t("whyChooseCard4.description"),
    },
    {
      title: t("whyChooseCard5.title"),
      description: t("whyChooseCard5.description"),
    },
    {
      title: t("whyChooseCard6.title"),
      description: t("whyChooseCard6.description"),
    },
  ];
  // If authenticated and on main platform, show school selection/creation page
  if (user && user.uid) {
    return (
      <Container maxWidth="md">
        <Box py={4}>
          <Typography
            variant="h3"
            component="h1"
            gutterBottom
            textAlign="center"
          >
            {t("selectOrCreateSubtitle")}
          </Typography>
          <Typography
            variant="body1"
            color="text.secondary"
            textAlign="center"
            component={"p"}
          >
            {t("chooseOrCreateSubtitle")}
          </Typography>

          {error && (
            <Alert severity="error" sx={{ mb: 3 }}>
              {error}
            </Alert>
          )}

          {/* My Schools */}
          <Box mb={4}>
            <Typography variant="h5" gutterBottom>
              {t("mySchoolsSubtitle")}
            </Typography>

            {schoolLoading ? (
              <Box display="flex" justifyContent="center" py={4}>
                <CircularProgress />
              </Box>
            ) : schools.length > 0 ? (
              <Stack spacing={2}>
                {schools.map((school) => (
                  <Card key={school.id} variant="outlined">
                    <CardContent>
                      <Typography variant="h6" gutterBottom>
                        {school.name}
                      </Typography>
                      <Chip
                        label={school.slug}
                        size="small"
                        color="primary"
                        variant="outlined"
                      />
                    </CardContent>
                    <CardActions>
                      <Button
                        variant="contained"
                        onClick={() => handleSelectSchool(school)}
                      >
                        {t("selectSchoolButtonText")}
                      </Button>
                    </CardActions>
                  </Card>
                ))}
              </Stack>
            ) : (
              <Typography color="text.secondary">
                {t("noSchoolsMessage")}
              </Typography>
            )}
          </Box>

          {/* Create New School */}
          <Box textAlign="center">
            <Button
              variant="contained"
              size="large"
              onClick={() => setShowCreateDialog(true)}
            >
              {t("createNewSchool")}
            </Button>
          </Box>

          {/* Create School Dialog */}
          <Dialog
            open={showCreateDialog}
            onClose={() => setShowCreateDialog(false)}
            maxWidth="sm"
            fullWidth
          >
            <DialogTitle> {t("createNewSchool")}</DialogTitle>
            <DialogContent>
              <Stack spacing={3} sx={{ mt: 1 }}>
                <TextField
                  label={t("schoolName")}
                  fullWidth
                  value={newSchool.name}
                  onChange={(e) => handleNameChange(e.target.value)}
                  placeholder="My Online School"
                />
                <TextField
                  label={t("schoolSlug")}
                  fullWidth
                  value={newSchool.slug}
                  onChange={(e) => handleSlugChange(e.target.value)}
                  placeholder="my-online-school"
                  helperText="This will be used in your school's URL"
                />
              </Stack>
            </DialogContent>
            <DialogActions>
              <Button onClick={() => setShowCreateDialog(false)}>
                {tActions("cancel")}
              </Button>
              <Button
                onClick={handleCreateSchool}
                variant="contained"
                disabled={
                  createLoading ||
                  !newSchool.name.trim() ||
                  !newSchool.slug.trim()
                }
              >
                {createLoading ? (
                  <CircularProgress size={20} />
                ) : (
                  "Create School"
                )}
              </Button>
            </DialogActions>
          </Dialog>
        </Box>
      </Container>
    );
  }
  return (
    <Container maxWidth="lg">
      {/* Hero Section */}
      <Box textAlign="center" py={8}>
        <Typography
          variant="h1"
          component="h1"
          gutterBottom
          sx={{
            fontSize: { xs: "2.5rem", md: "3.5rem" },
            fontWeight: "bold",
            background: "linear-gradient(45deg, #2196F3 30%, #21CBF3 90%)",
            backgroundClip: "text",
            WebkitBackgroundClip: "text",
            WebkitTextFillColor: "transparent",
          }}
        >
          {t("welcome")} {PLATFORM_NAME}
        </Typography>

        <Typography
          variant="h4"
          color="text.secondary"
          component={"p"}
          sx={{ fontSize: { xs: "1.25rem", md: "1.5rem" }, mb: 4 }}
        >
          {t("headline")}
        </Typography>

        <Typography
          variant="body1"
          color="text.secondary"
          component={"p"}
          sx={{ maxWidth: "600px", mx: "auto", mb: 6 }}
        >
          {t("subheadline")}
        </Typography>

        <Box mb={6}>
          <GoogleSignInButton onClick={onLogin} />
        </Box>
      </Box>

      {/* Features Section */}
      <Box py={6}>
        <Typography variant="h3" textAlign="center" gutterBottom mb={6}>
          {t("whyChoose")} {PLATFORM_NAME}?
        </Typography>

        {/* Features Grid using CSS Grid */}
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: {
              xs: "1fr",
              sm: "repeat(2, 1fr)",
              md: "repeat(3, 1fr)",
            },
            gap: 4,
            mb: 6,
          }}
        >
          {whyChooseCards.map((card, index) => (
            <Card key={index} sx={{ height: "100%", textAlign: "center" }}>
              <CardContent sx={{ p: 4 }}>
                <Typography variant="h5" gutterBottom color="primary">
                  {card.title}
                </Typography>
                <Typography color="text.secondary">
                  {card.description}
                </Typography>
              </CardContent>
            </Card>
          ))}
        </Box>
      </Box>

      {/* Call to Action */}
      <Box textAlign="center" py={8}>
        <Typography variant="h4" gutterBottom>
          {t("ctaTitle")}
        </Typography>
        <Typography
          variant="body1"
          color="text.secondary"
          component={"p"}
          sx={{ mb: 4 }}
        >
          {t("ctaSubTitle")}
        </Typography>
        <GoogleSignInButton onClick={onLogin} />
      </Box>
    </Container>
  );
}
